/**
 * Paid Media — Learn-Mode inline tips.
 *
 * Covers `routes/social/PaidMedia*.tsx`, `routes/social/_sections/paid-media/*`,
 * and the top-level `components/PaidMediaCampaignKpiStrip.tsx` /
 * `PaidMediaChooseClient.tsx`.
 */

import type { LearnTip } from "./types";

export const paidMediaTips: LearnTip[] = [
  {
    key: "paid-media-choose-client",
    title: "Choose a Client",
    body: "Paid media is tracked per client. Pick one here, or use the client switcher at the top of the app, to see its campaigns, leads, or ad accounts.",
  },
  {
    key: "paid-media-kpi-strip",
    title: "Campaign KPI Summary",
    body: "The headline numbers for this client's paid campaigns over the selected period — spend, reach, and results, at a glance.",
  },
  {
    key: "paid-media-campaign-filters",
    title: "Searching Campaigns",
    body: "Search campaigns by name or ID, and switch between table and card view.",
  },
  {
    key: "paid-media-period-compare",
    title: "Comparison Period",
    body: "Choose how far back to look. Every metric on this page compares the selected period against the one before it.",
  },
  {
    key: "paid-media-campaign-table",
    title: "Campaigns",
    body: "Every ad campaign for this client — its health, status, objective, and budget pacing. Click a column header to sort.",
  },
  {
    key: "paid-media-attention-callout",
    title: "Campaigns Needing Attention",
    body: "Flags active campaigns with a health issue — like an underspending budget or a disapproved ad — so they don't get missed. Click through to filter the list down to just those.",
  },
  {
    key: "paid-media-ads-gallery",
    title: "Ads Gallery",
    body: "Every ad creative running for this client, in one visual gallery — useful for a quick creative review without opening Meta Ads Manager for each one.",
  },
  {
    key: "paid-media-audit-findings",
    title: "Audit Findings",
    body: "Automated checks against this client's paid accounts, grouped by severity — errors first, then warnings, with passed checks collapsed. Each finding explains what's wrong and which campaign it affects.",
  },
  {
    key: "paid-media-speed-to-lead",
    title: "Speed to Lead",
    body: "How quickly new leads from paid campaigns get a first response — a key metric for ad quality, since slow follow-up usually means a wasted lead.",
  },
  {
    key: "paid-media-leads",
    title: "Leads",
    body: "Every lead generated by this client's paid campaigns, pulled directly from Meta — form fills, calls, and messages, with their follow-up status.",
  },
  {
    key: "paid-media-lead-pipeline",
    title: "Filter by Status",
    body: "Narrow the lead list down to a specific stage of your follow-up pipeline, such as new, contacted, or converted.",
  },
  {
    key: "paid-media-leads-export",
    title: "Exporting Leads",
    body: "Downloads the currently filtered leads as a spreadsheet. Optionally include contact details — turn this off if you're sharing the export outside your team.",
  },
  {
    key: "paid-media-audit-log",
    title: "Audit Log",
    body: "A permanent record of every paid-media change for this client — status flips, budget edits, account links, lead PII unmasks and audit runs. Filter by who, what or when, open a row to see the before and after, and export the filtered list as a spreadsheet. Sensitive values are masked.",
  },
  {
    key: "paid-media-webhook-events",
    title: "Webhook Deliveries",
    body: "A read-only log of Meta page webhook deliveries for this client — comments and feed activity, with their signature and processing status. Lead-form submissions are captured separately and shown on the Leads page.",
  },
];
